import type {
    AutoReplyProcessingResult,
    ReviewAnswerDraftStatus,
    SelectedAutoReplyRunSkippedReviewCode,
} from '@/contracts/marketplace/auto-replies';

export type AutoReplyLabelLocale = 'ru' | 'en';

const DRAFT_STATUS_LABELS: Record<AutoReplyLabelLocale, Record<ReviewAnswerDraftStatus, string>> = {
    ru: {
        pending_approval: 'Ожидает модерации',
        sending: 'Ответ отправляется',
        sent: 'Ответ отправлен',
        superseded: 'Черновик заменен',
        failed: 'Ошибка автоответа',
    },
    en: {
        pending_approval: 'Awaiting moderation',
        sending: 'Sending reply',
        sent: 'Reply sent',
        superseded: 'Draft superseded',
        failed: 'Auto-reply failed',
    },
};

const PROCESSING_RESULT_LABELS: Record<AutoReplyLabelLocale, Partial<Record<AutoReplyProcessingResult, string>>> = {
    ru: {
        processing: 'Автоответ формируется',
        superseded_by_user_request: 'Заменено пользовательским AI-ответом',
        skipped_ai_analysis_failed: 'Автоответ пропущен: ИИ-анализ не завершился',
        skipped_offer_not_accepted: 'Автоответ пропущен: сначала примите оферту',
    },
    en: {
        processing: 'Auto reply processing',
        superseded_by_user_request: 'Superseded by user AI reply',
        skipped_ai_analysis_failed: 'Auto reply skipped: AI analysis did not finish',
        skipped_offer_not_accepted: 'Auto reply skipped: accept the offer first',
    },
};

const SELECTED_RUN_SKIPPED_REVIEW_LABELS: Record<
    AutoReplyLabelLocale,
    Record<SelectedAutoReplyRunSkippedReviewCode, string>
> = {
    ru: {
        already_answered: 'Отзыв уже с ответом',
        active_draft: 'Для отзыва уже есть активный черновик ответа',
        not_review: 'Автоответы применяются только к отзывам без ответа',
        not_found: 'Отзыв не найден',
    },
    en: {
        already_answered: 'Review already has an answer',
        active_draft: 'Review already has an active reply draft',
        not_review: 'Auto replies apply only to unanswered reviews',
        not_found: 'Review not found',
    },
};

const PROGRESS_CARD_LABELS: Record<
    AutoReplyLabelLocale,
    {
        title: string;
        success: string;
        skipped: string;
        errors: string;
        completed: string;
        failed: string;
        canceled: string;
        pendingEta: string;
        remainingPrefix: string;
        genericError: string;
    }
> = {
    ru: {
        title: 'Автоответы по выбранным',
        success: 'успешно',
        skipped: 'пропущено',
        errors: 'ошибки',
        completed: 'Завершено',
        failed: 'Завершено с ошибкой',
        canceled: 'Отменено',
        pendingEta: 'Оценка времени появится после первых результатов',
        remainingPrefix: 'Осталось',
        genericError: 'Ошибка автоответов',
    },
    en: {
        title: 'Auto replies for selected',
        success: 'success',
        skipped: 'skipped',
        errors: 'errors',
        completed: 'Completed',
        failed: 'Finished with an error',
        canceled: 'Canceled',
        pendingEta: 'Time estimate will appear after the first results',
        remainingPrefix: 'Remaining',
        genericError: 'Auto replies failed',
    },
};

const SELECTED_RUN_LABELS: Record<
    AutoReplyLabelLocale,
    {
        action: string;
        title: string;
    }
> = {
    ru: {
        action: 'Автоответы',
        title: 'Автоответы по выбранным отзывам',
    },
    en: {
        action: 'Auto replies',
        title: 'Auto replies for selected reviews',
    },
};

export const getReviewAnswerDraftStatusLabel = (
    status: ReviewAnswerDraftStatus,
    locale: AutoReplyLabelLocale = 'ru',
): string => DRAFT_STATUS_LABELS[locale]?.[status] ?? DRAFT_STATUS_LABELS.ru[status];

export const getAutoReplyProcessingResultLabel = (
    result: AutoReplyProcessingResult,
    locale: AutoReplyLabelLocale = 'ru',
): string => PROCESSING_RESULT_LABELS[locale]?.[result] ?? PROCESSING_RESULT_LABELS.ru[result] ?? result;

export const getSelectedAutoReplyRunSkippedReviewLabel = (
    code: SelectedAutoReplyRunSkippedReviewCode,
    locale: AutoReplyLabelLocale = 'ru',
): string => SELECTED_RUN_SKIPPED_REVIEW_LABELS[locale]?.[code] ?? SELECTED_RUN_SKIPPED_REVIEW_LABELS.ru[code];

export const getAutoReplyProgressCardLabels = (locale: AutoReplyLabelLocale = 'ru') =>
    PROGRESS_CARD_LABELS[locale] ?? PROGRESS_CARD_LABELS.ru;

export const getAutoReplyProgressCardTitle = (locale: AutoReplyLabelLocale = 'ru'): string =>
    getAutoReplyProgressCardLabels(locale).title;

export const getAutoReplyProgressFailedLabel = (locale: AutoReplyLabelLocale = 'ru'): string =>
    getAutoReplyProgressCardLabels(locale).failed;

export const getAutoReplyProgressCanceledLabel = (locale: AutoReplyLabelLocale = 'ru'): string =>
    getAutoReplyProgressCardLabels(locale).canceled;

export const getSelectedAutoRepliesRunLabels = (locale: AutoReplyLabelLocale = 'ru') =>
    SELECTED_RUN_LABELS[locale] ?? SELECTED_RUN_LABELS.ru;
